import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";
import { Button, SvgIconHandler } from "../../atoms";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShareTemplate } from "../../../arrow";
import { SocialShare } from "@quintype/components";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { TableView } from "./table-view";
import { YearDropdown } from "../mpw-detail-page/year-drop-down";
import { RankingColumns, RankingSubColumns } from "./ranking-columns";
import SearchIcon from "../rankings-table/search";
import { bestBSchoolsStaticData } from "./static-info";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { trackLoadMoreClick } from "../../utils";
import { MNC_500_DESCRIPTIONS } from "../../../../../config/entity-ranking-descriptions";

import "./ranking-table.m.css";

export const Mnc500 = ({ data }) => {
  const { staticData, rankingEntitySlug, entitiesByYearJson, activeYear, mnc500, totalSnapshotCount, fullDataUrl } = data;
  const initialSnapshots = mnc500?.snapshots || [];
  const { label } = staticData;
  const pageDescription = MNC_500_DESCRIPTIONS[activeYear];

  const { LOAD_MORE, READ_MORE, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const [enableLoadMore, setEnableLoadMore] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [yearDropdownActive, setYearDropdownActive] = useState(false);
  const [visibleItemsForList, setVisibleItemsForList] = useState(10);
  const [showEntities, setShowEntities] = useState(initialSnapshots);
  const [searchCount, setSearchCount] = useState(totalSnapshotCount || initialSnapshots.length);
  const [fullUrl, setFullUrl] = useState("");
  const [currentView, setCurrentView] = useState("overall");
  const [fullSnapshots, setFullSnapshots] = useState(null);
  const [isFetchingFullData, setIsFetchingFullData] = useState(false);

  const subheadlineRef = useRef(null);
  const allSnapshots = fullSnapshots || initialSnapshots;

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["config", "isWebView"]) === true;

  const expandBio = () => setReadMoreClicked(true);

  const loadFullSnapshots = async () => {
    if (fullSnapshots) return fullSnapshots;
    if (!fullDataUrl) return initialSnapshots;

    try {
      setIsFetchingFullData(true);
      const response = await fetch(fullDataUrl);
      const json = await response.json();
      const snapshots = json?.snapshots || [];
      setFullSnapshots(snapshots);
      return snapshots;
    } catch (err) {
      console.error("Error fetching MNC 500 data:", err);
      return initialSnapshots;
    } finally {
      setIsFetchingFullData(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const searchTerm = searchInputValue;
    if (searchTerm && currentView === "overall" && !fullSnapshots) {
      await loadFullSnapshots();
    }
    setSearchText(searchTerm);
  };

  const getMoreStories = async () => {
    if (isFetchingFullData) return;

    trackLoadMoreClick({
      linkCategory: `${rankingEntitySlug}-${activeYear}`,
      category: "ranking",
      subCategory: rankingEntitySlug,
      templateName: "rankdetail",
    });
    if (currentView === "overall" && !fullSnapshots) {
      await loadFullSnapshots();
    }
    setVisibleItemsForList((prev) => prev + 10);
  };

  const includesCaseInsensitive = (str = "", searchTerm = "") =>
    String(str || "")
      .toLowerCase()
      .includes(
        String(searchTerm || "")
          .trim()
          .toLowerCase()
      );

  // Track all ranking detail interactions
  let isTracking = false;
  const trackRankingInteraction = (action, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_ranking_detail_interactions",
        event_action: action,
        link_text: value,
        ...(position && { link_position: position }),
        category: "rankings",
        sub_category: data?.rankingEntitySlug || "NA",
        template_name: "rankdetail",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  useEffect(() => {
    setVisibleItemsForList(10);
    if (typeof window !== "undefined") {
      setFullUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    const totalItems = searchText
      ? searchCount
      : fullSnapshots
        ? allSnapshots.length
        : totalSnapshotCount || allSnapshots.length;
    setEnableLoadMore(currentView === "overall" && visibleItemsForList < totalItems);
  }, [allSnapshots.length, currentView, fullSnapshots, searchCount, searchText, totalSnapshotCount, visibleItemsForList]);

  useEffect(() => {
    if (!isDesktop && subheadlineRef.current) {
      const bioHeight = window?.getComputedStyle(subheadlineRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = subheadlineRef.current.scrollHeight;
      subheadlineRef.current.style.height = `${lineHeightForThree}px`;
      subheadlineRef.current.style.overflow = "hidden";

      if (readMoreClicked) {
        subheadlineRef.current.style.height = `${contentHeight}px`;
      }
    }
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    const entities = allSnapshots;

    if (searchText) {
      const filterFn = (snapshot) => includesCaseInsensitive(snapshot.company, searchText);
      const filteredSnapshots = entities.filter(filterFn);
      setSearchCount(filteredSnapshots.length);
      setShowEntities(filteredSnapshots);
    } else {
      setSearchCount(entities.length);
      setShowEntities(entities);
    }
  }, [allSnapshots, searchText]);

  const fetchData = async (group) => {
    try {
      const fetchJson = async (url) => {
        const response = await fetch(url);
        return response.json();
      };

      return group.urls.length === 1 ? await fetchJson(group.urls[0]) : await Promise.all(group.urls.map(fetchJson));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (currentView === "overall") {
      return;
    }

    const activeViewObj = bestBSchoolsStaticData(activeYear).find((obj) => obj.key === currentView);

    setSearchText("");
    setSearchInputValue("");

    const fetchAndSetData = async () => {
      const data = await fetchData(activeViewObj);
      setShowEntities(data);
    };

    fetchAndSetData();
  }, [allSnapshots, currentView]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "rankings",
      sub_category: "mnc500",
      content_id: "NA",
      template_name: "rankdetail",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  const renderTableView = (mnc, activeYear, title = null, columns = RankingColumns, subColumns = RankingSubColumns) => (
    <div styleName={`page-body`}>
      {title && <h2>{title.toLowerCase()}</h2>}
      <TableView
        mnc500={mnc}
        visibleItemsForList={visibleItemsForList}
        rankingEntitySlug={rankingEntitySlug}
        columns={columns}
        currentView={currentView}
        subcolumns={subColumns}
        activeYear={activeYear}
      />
    </div>
  );

  return (
    <div styleName="entity-detail-page">
      <div
        styleName="banner"><img src={!isDesktop ? "/images/banners/MNC500Mobile.png" : "/images/banners/MNC500Desktop.png"} alt={label} title={label} loading="lazy" width={1470} height={417} /></div>
      <div className="container" styleName="content-wrapper">
        <div styleName="header">
          <div styleName="header-content">
            <div styleName="headline">
              <span styleName="yeardrop">
                <YearDropdown
                  isDesktop={isDesktop}
                  yearDropdownActive={yearDropdownActive}
                  setYearDropdownActive={setYearDropdownActive}
                  yearData={[2026, 2025]}
                  rankingEntitySlug={rankingEntitySlug}
                  entitiesByYearJson={entitiesByYearJson}
                  page="b-schools"
                  activeYear={activeYear}
                />
              </span>
              <h1 styleName="text">{`${label || top15Label} ${activeYear}`}</h1>
            </div>
            <div styleName="sub-headline clamped-text" ref={subheadlineRef}>
              {pageDescription}
            </div>
            {!readMoreClicked && !isDesktop && (
              <button onClick={expandBio} styleName="read-more">
                {READ_MORE}
              </button>
            )}
          </div>
        </div>
        <div styleName="search-row">
          <form onSubmit={handleSearch}>
            <div styleName="search">
              <div styleName="input">
                {searchText !== "" && searchInputValue !== "" && (
                  <span
                    onClick={() => {
                      setSearchInputValue("");
                      setSearchText("");
                    }}
                    styleName="best-Bschools-clearSearch"
                  >
                    <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                  </span>
                )}
                <input
                  type="text"
                  name="search"
                  placeholder="Search"
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  styleName={searchInputValue !== "" ? "input-bar" : ""}
                />
              </div>
              <Button customStyleName={"bg-brand-primary"} onClickFunction={handleSearch}>
                {isDesktop ? "Search" : <SearchIcon />}
              </Button>
            </div>
          </form>
          {!isWebView && (
            <div styleName="list-grid-toggle">
              <SocialShare
                template={(props) =>
                  SocialShareTemplate({
                    ...props,
                    onShareClick: (platform) =>
                      trackRankingInteraction("share_click", platform?.toLowerCase() || "share"),
                  })
                }
                fullUrl={fullUrl}
                title={`${label} ${activeYear}`}
                size={18}
              />
            </div>
          )}
        </div>
        {!showEntities ||
          showEntities.length === 0 ||
          (showEntities.snapshots && showEntities.snapshots.length === 0) ? (
          <div styleName="oops-text">
            <span>
              {NO_RESULT_FOUND}'{searchText}' <br /> {TRY_AGAIN}
            </span>
          </div>
        ) : (
          <>{currentView === "overall" && renderTableView(showEntities, activeYear)}</>
        )}

        {enableLoadMore ? (
          <div styleName="load-more-button">
            <Button onClickFunction={() => getMoreStories()}>{isFetchingFullData ? "Loading..." : LOAD_MORE}</Button>
          </div>
        ) : (
          <div style={{ marginBottom: isDesktop ? "" : "16px" }} />
        )}
        {activeYear === '2026' && (
          <div styleName="footnotes">
            <strong>SOURCE:</strong> CAPITALINE, TOFLER, TRACXN, FORTUNE INDIA RESEARCH
          </div>
        )}
        {!isAdfreePage && (
          <ListingAdSlot
            adType="above_footer"
            pageType="mnc-500-detail"
            adStyleName="ad-slot-size-728x90-300x250"
            index={3}
          />
        )}
      </div>
    </div>
  );
};

Mnc500.propTypes = {
  data: PropTypes.object,
};
